
import React, { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTasks } from '@/context/TaskContext';
import { useTimerContext } from '@/context/TimerContext';
import { supabase } from '@/integrations/supabase/client';
import GoalSettingSection from './components/GoalSettingSection';
import WeeklyChart from './components/WeeklyChart';
import { calculateWeeklyProgress, calculateChartDomain, formatTime } from './utils/chartUtils';

interface WeeklyProgressProps {
  tasks: any[];
  dailyTarget: number;
  onTargetUpdate?: (newTarget: number) => void;
}

const WeeklyProgress: React.FC<WeeklyProgressProps> = ({ tasks, dailyTarget, onTargetUpdate }) => {
  const { user } = useAuth();
  const { activeTask } = useTasks();
  const { timer } = useTimerContext();
  const [tempGoal, setTempGoal] = useState(dailyTarget);
  const [isSaving, setIsSaving] = useState(false);
  const [isGoalSectionExpanded, setIsGoalSectionExpanded] = useState(false);
  const [dailyProgress, setDailyProgress] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Calculate daily progress for the week using time entries
  useEffect(() => {
    const fetchWeeklyProgress = async () => {
      setLoading(true);
      try {
        const progress = await calculateWeeklyProgress(user?.id || null, dailyTarget);
        setDailyProgress(progress);
        console.log('Updated weekly progress:', progress);
      } catch (error) {
        console.error('Error calculating weekly progress:', error);
        setDailyProgress([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWeeklyProgress();
  }, [user?.id, dailyTarget, refreshKey]);

  // Listen for real-time changes to time_entries table
  useEffect(() => {
    if (!user?.id) return;

    console.log('Setting up real-time listener for time entries');

    const channel = supabase
      .channel('weekly_progress_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'time_entries',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Time entry change detected for weekly progress:', payload);
          // Refresh the chart data when time entries change
          setRefreshKey(prev => prev + 1);
        }
      )
      .subscribe((status) => {
        console.log('Weekly progress subscription status:', status);
      });

    return () => {
      console.log('Cleaning up weekly progress subscription');
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Refresh progress when active task time changes (for real-time updates)
  useEffect(() => {
    if (activeTask?.totalTimeSpent) {
      const refreshProgress = async () => {
        try {
          const progress = await calculateWeeklyProgress(user?.id || null, dailyTarget);
          setDailyProgress(progress);
        } catch (error) {
          console.error('Error refreshing weekly progress:', error);
        }
      };
      
      // Debounce the refresh to avoid too many calls
      const timeoutId = setTimeout(refreshProgress, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [activeTask?.totalTimeSpent, user?.id, dailyTarget]);

  // Add current session time to today's progress for real-time updates
  const enhancedDailyProgress = React.useMemo(() => {
    if (!timer.isRunning || timer.timerType !== 'pomodoro' || !activeTask) {
      return dailyProgress;
    }

    const today = new Date();
    const todayString = today.toLocaleDateString('en-US', { weekday: 'short' });
    
    // Calculate elapsed time in current session (in minutes)
    const totalSessionSeconds = (timer.settings.pomodoro * 60) - (timer.minutes * 60 + timer.seconds);
    const currentSessionMinutes = Math.floor(totalSessionSeconds / 60);

    return dailyProgress.map(day => {
      if (day.day === todayString && day.isToday) {
        return {
          ...day,
          minutes: day.minutes + currentSessionMinutes
        };
      }
      return day;
    });
  }, [dailyProgress, timer.isRunning, timer.timerType, timer.minutes, timer.seconds, timer.settings.pomodoro, activeTask]);

  // Calculate weekly statistics using enhanced progress
  const totalWeeklyMinutes = enhancedDailyProgress.reduce((sum, day) => sum + day.minutes, 0);
  const averageDailyMinutes = totalWeeklyMinutes / 7;
  const isAheadOfTarget = averageDailyMinutes > tempGoal;

  // Calculate chart domain using enhanced progress
  const { roundedMax, yAxisTicks } = calculateChartDomain(enhancedDailyProgress, dailyTarget);

  const handleGoalUpdate = async () => {
    if (!user || isSaving || tempGoal === dailyTarget) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          daily_focus_target: tempGoal
        })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating daily goal:', error);
        return;
      }

      if (onTargetUpdate) {
        onTargetUpdate(tempGoal);
      }

    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">
            Weekly progress
          </h4>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
          <div className="animate-pulse">
            <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">
          Weekly progress
        </h4>
        <button
          onClick={() => setIsGoalSectionExpanded(!isGoalSectionExpanded)}
          className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
        >
          <Pencil size={14} />
          <span>Daily goal: {formatTime(dailyTarget)}</span>
        </button>
      </div>

      {isGoalSectionExpanded && (
        <GoalSettingSection
          dailyTarget={dailyTarget}
          tempGoal={tempGoal}
          setTempGoal={setTempGoal}
          isSaving={isSaving}
          onGoalUpdate={handleGoalUpdate}
        />
      )}

      <div className="w-full">
        <WeeklyChart
          dailyProgress={enhancedDailyProgress}
          isAheadOfTarget={isAheadOfTarget}
          roundedMax={roundedMax}
          yAxisTicks={yAxisTicks}
        />
      </div>
    </div>
  );
};

export default WeeklyProgress;
